// src/component/PAR/mockData/parData.js
// Single source of truth for all PAR (Planning & Reporting) tabs

// ─── States ───────────────────────────────────────────────────────────────────
export const STATE_LGA_MAP = {
  Kano:    ["Kano Municipal", "Garko", "Bebeji", "Doguwa", "Gwarzo"],
  Borno:   ["Maiduguri", "Ngala", "Bama", "Konduga", "Gwoza"],
  Kaduna:  ["Kaduna North", "Kaduna South", "Zaria", "Igabi"],
  Lagos:   ["Ikeja", "Surulere", "Eti-Osa", "Ajeromi"],
  Rivers:  ["Port Harcourt", "Obio-Akpor", "Eleme", "Khana"],
  Ogun:    ["Abeokuta North", "Abeokuta South", "Ifo", "Sagamu"],
  FCT:     ["Abuja Municipal", "Bwari", "Gwagwalada", "Kuje"],
  Adamawa: ["Yola North", "Yola South", "Mubi North", "Numan"],
  Bauchi:  ["Bauchi", "Toro", "Ningi", "Dass"],
  Sokoto:  ["Sokoto North", "Sokoto South", "Wamako", "Dange"],
};
export const ALL_STATES = Object.keys(STATE_LGA_MAP);

// ─── Tab definitions ──────────────────────────────────────────────────────────
export const TABS = [
  { key: "workplans",    label: "Workplan & Activities"  },
  { key: "reports",      label: "Activity Reports"       },
  // { key: "publications", label: "Publication Registry"   },
  { key: "validation",   label: "PAR Validation History" },
];

// ─── Workplan stats (spec-aligned) ───────────────────────────────────────────
export const workplanSummary = {
  total:            { value: "6.7k", trend: "(5.6k) 20% lesser last month", trendLabel: "Today (Green) vs. last report period (Grey)" },
  budgetCommitted:  { value: "₦8.4M", trend: "(₦5.6M) +", trendLabel: "Today (Green) vs. last report period (Grey)" },
  plansForReview:   { value: 10, label: "Total plans approaching end date without completion", redThreshold: 5 },
};

export const byStatusData = [
  { label: "Draft",     value: 70,  color: "#374151" },
  { label: "Approved",  value: 90,  color: "#3b82f6" },
  { label: "Active",    value: 50,  color: "#22c55e" },
  { label: "Completed", value: 100, color: "#16a34a" },
  { label: "Suspended", value: 40,  color: "#eab308" },
];

// ─── Activity Reports stat cards ──────────────────────────────────────────────
export const reportStats = {
  submitted:            48,
  completionRate:       72,
  onTimeRate:           88,
  avgOutputAchievement: 85,
  notStarted:           3,
};

// ─── Workplans ────────────────────────────────────────────────────────────────
const planTypes      = ["Annual", "Medium Term", "Emergency"];
const wpStatuses     = ["Draft", "Approved", "Active", "Completed", "Suspended", "Cancelled"];
const approvalStatuses = ["Submitted", "Pending approval", "Approved", "Rejected"];
const authLevels     = ["Director", "DG", "Minister"];
const geoScopes      = ["National", "State Specific", "LGA Targeted"];
const budgets        = ["₦13.8M", "₦91.6M", "₦4.5B", "₦21.8M", "₦35.8M"];
const periods        = ["2026-Q2", "2026-Q1", "2024-Q3", "2025-Q1", "2024-Q2"];
const departments    = ["Animal Production", "Veterinary Services", "Policy & Planning", "Research & Statistics", "Finance & Admin", "Livestock Extension"];
const programmes     = ["LFSP 2025", "SHIGA Programme", "NAFIS Phase III", "NAPEP Livestock", "World Bank Rural Dev"];

export const workplansData = Array.from({ length: 20 }, (_, i) => ({
  id:                 `WP${i + 1}`,
  institution:        "FMLD",
  title:              `${["Livestock Vaccination", "Extension Services", "Market Dev", "Disease Surveillance", "Feed Support"][i % 5]} Workplan`,
  planType:           planTypes[i % planTypes.length],
  period:             periods[i % periods.length],
  focusArea:          ["Animal Health", "Production", "Marketing", "Nutrition", "Research"][i % 5],
  department:         departments[i % departments.length],
  budget:             budgets[i % budgets.length],
  planOwner:          ["Adebayo Musa", "Chioma Okafor", "Ibrahim Lawal", "Ngozi Eze", "Usman Bello"][i % 5],
  status:             wpStatuses[i % wpStatuses.length],
  approvalStatus:     approvalStatuses[i % approvalStatuses.length],
  approvingAuthority: authLevels[i % authLevels.length],
  geographicScope:    geoScopes[i % geoScopes.length],
  linkedProgramme:    programmes[i % programmes.length],
  state:              ALL_STATES[i % ALL_STATES.length],
}));

// ─── Workplan Activities ──────────────────────────────────────────────────────
const actCategories  = ["Training", "Field Visit", "Survey", "Procurement", "Meeting", "Construction", "Campaign", "Technical Assistance", "Other"];
const actStatuses    = ["Completed", "Planned", "In Progress", "Delayed", "Cancelled"];
const completionPcts = [100, null, 36, 100, null, 100, 36, 24, null, 100];
const lgas           = ["Dala", "Ngala", "Dala", "Ngala", "Dala", "Ngala", "Dala", "Ngala"];

export const activitiesData = Array.from({ length: 20 }, (_, i) => ({
  id:                 `ACT${i + 1}`,
  workplan:           `WP${(i % 5) + 1}`,
  title:              `${actCategories[i % actCategories.length]} Activity`,
  category:           actCategories[i % actCategories.length],
  state:              i % 2 === 0 ? "Kano" : "Borno",
  startDate:          "04-04-2026",
  endDate:            i % 3 === 1 ? "---" : "19-04-2026",
  budget:             budgets[i % budgets.length],
  responsibleUnits:   ["Dept of Animal Health", "Vet Services Unit", "Extension Division", "Research Dept"][i % 4],
  status:             actStatuses[i % actStatuses.length],
  lga:                lgas[i % lgas.length],
  actualExpenditure:  budgets[(i + 2) % budgets.length],
  completionPct:      completionPcts[i % completionPcts.length],
  targetBeneficiaries: [500, 1200, 3000, 750, 2100, 450, 900, 1500, 600, 2000][i % 10],
  evidenceRef:        `EV-${2026}-${String(i + 1).padStart(3, '0')}`,
}));

// ─── Activity Reports ─────────────────────────────────────────────────────────
const execStatuses   = ["Completed", "Partially Completed", "Not Started", "Delayed", "Cancelled"];
const outputTypes    = ["Farmers Trained", "Animals Vaccinated", "Facilities Inspected", "Officers Deployed", "Reports Generated"];
const reportedBys    = ["Sunday Agada", "Brick & Lace", "Joshua Jibril", "Amina Bello", "Tunde Adesanya"];
const validatedBys   = ["Ariyo Oyeyemi", "Ofe Matthews", "Bukola Adebayo", "Ngozi Eze", "Michael Jackson"];
const reportPeriods  = ["2026 - Q2", "2026 - Q1", "2025 - Q4", "2025 - Q3"];

export const reportsData = Array.from({ length: 20 }, (_, i) => ({
  id:               `RPT${i + 1}`,
  activityId:       `ACT${(i % 10) + 1}`,
  reportingPeriod:  reportPeriods[i % reportPeriods.length],
  startDate:        "04-04-2026",
  endDate:          i % 3 === 1 ? "---" : "19-04-2026",
  executionStatus:  execStatuses[i % execStatuses.length],
  expenditure:      budgets[i % budgets.length],
  outputsAchieved:  outputTypes[i % outputTypes.length],
  outputsCount:     [45, 1200, 8, 12, 36][i % 5],
  varianceExpl:     i % 5 === 2 || i % 5 === 3 ? "Logistics delays due to flooding in access roads" : "On track per approved workplan",
  reportedBy:       reportedBys[i % reportedBys.length],
  validatedBy:      validatedBys[i % validatedBys.length],
  submissionDate:   "21-05-2026",
  outputsTarget:    [50, 1500, 10, 15, 40][i % 5],
  state:            i % 2 === 0 ? "Kano" : "Borno",
}));

// ─── Publication Registry ─────────────────────────────────────────────────────
const pubTypes     = ["Statistical Bulletin", "Annual Report", "Survey Report", "Policy Brief", "Technical Guideline", "Training Manual", "Press Release", "Data Abstract", "Programme Report"];
const pubStatuses  = ["Completed", "In Progress", "Planned", "Delayed"];
const accessLevels = ["Public", "Internal", "Restricted"];

export const publicationsData = Array.from({ length: 20 }, (_, i) => ({
  id:             `PUB${i + 1}`,
  title:          `${pubTypes[i % pubTypes.length]} — ${periods[i % periods.length]}`,
  pubType:        pubTypes[i % pubTypes.length],
  status:         pubStatuses[i % pubStatuses.length],
  docRefNumber:   `FMLD-${2026}-PUB-${String(i + 1).padStart(3, '0')}`,
  accessLevel:    accessLevels[i % accessLevels.length],
  linkedWorkplan: i % 3 === 0 ? `WP${(i % 5) + 1}` : "—",
  department:     departments[i % departments.length],
  publishedDate:  i % pubStatuses.length === 0 ? "15-03-2026" : "—",
  state:          ALL_STATES[i % ALL_STATES.length],
}));

// ─── PAR Validation History ───────────────────────────────────────────────────
const validationTypes   = ["Workplan", "Activity Reports", "Publication"];
const validationActions = ["submission_check", "content_review", "technical_review", "final_approval"];
const prevStatuses      = ["Draft", "Pending approval", "Active", "Submitted"];
const newStatuses       = ["Approved", "Rejected", "Completed", "Active", "Suspended"];

const validationTitles = [
  "Livestock Vaccination Workplan — WP1",
  "Q1 Activity Report — ACT3",
  "Statistical Bulletin 2026-Q1",
  "Extension Services Workplan — WP2",
  "Q2 Activity Report — ACT7",
  "Annual Report 2025",
  "Market Dev Workplan — WP3",
  "Disease Surveillance Activity Report",
  "Policy Brief Publication",
  "Feed Support Workplan — WP4",
  "Field Visit Activity Report — ACT5",
  "Survey Report 2026-Q2",
  "Emergency Workplan — WP5",
  "Training Activity Report — ACT9",
  "Programme Report 2026",
];

export const validationData = Array.from({ length: 15 }, (_, i) => ({
  id:              `VAL${i + 1}`,
  title:           validationTitles[i],
  date:            ["Apr 14, 18:30", "Apr 10, 09:15", "Mar 28, 14:00", "Mar 22, 11:45", "Mar 15, 16:30"][i % 5],
  type:            validationTypes[i % validationTypes.length],
  previousStatus:  prevStatuses[i % prevStatuses.length],
  newStatus:       newStatuses[i % newStatuses.length],
  actionBy:        ["Ibukun Ademola", "Chidi Okonkwo", "Fatima Sule", "David Obi"][i % 4],
  validatedBy:     validatedBys[i % validatedBys.length],
  validationAction: validationActions[i % validationActions.length],
  comments:        i % newStatuses.length === 1
    ? "Record rejected due to missing beneficiary data. Resubmit with complete details."
    : "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip",
  fullDate:        ["14/04/2026", "10/04/2026", "28/03/2026", "22/03/2026", "15/03/2026"][i % 5],
  recordRef:       `https://nlims.fmld.gov.ng/records/${validationTypes[i % validationTypes.length].toLowerCase().replace(/\s+/g, '-')}/${i + 1}`,
  state:           ALL_STATES[i % ALL_STATES.length],
}));
